import { useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { Anchor, Box, Stack, Button, Divider, Grid, Paper as MantinePaper, Text, Title } from "@mantine/core";
import { Link } from "react-router-dom";
import styles from "./styles/Home.module.css";
import { useStores } from "@/hooks/useStores";
import { ContinueListening } from "@/components/episode/ContinueListening";
import { SignInPrompt } from "@/components/auth/SignInPrompt";
import { EpisodeRow } from "@/components/episode/EpisodeRow";
import rowStyles from "@/components/episode/styles/EpisodeRow.module.css";
import { FeaturedCollections } from "@/components/collection/FeaturedCollections";
import { PopularPodcasts } from "@/components/podcast/PopularPodcasts";
import { PopularEpisodes } from "@/components/episode/PopularEpisodes";
import { getPodcastImage, ImageSizes } from "@/core/helpers";
import { ArtworkMorph } from "@/components/podcast/ArtworkMorph";
import { useArtworkMorphSource } from "@/hooks/useArtworkMorph";
import { useSEO } from "@/hooks/useSEO";
import { canonicalUrl } from "@/core/seo";
import { Urls } from "@/core/urls";

const GalleryCover = ({ podcast, index }: { podcast: Podcast; index: number }) => {
    const { name, markSource } = useArtworkMorphSource(podcast.id);

    return (
        <Link aria-label={podcast.name} className={styles.cover} onClick={markSource} to={`/podd/${podcast.slug}`}>
            <ArtworkMorph name={name}>
                <img
                    alt=""
                    decoding="async"
                    fetchPriority={index === 0 ? "high" : "auto"}
                    height={60}
                    loading="eager"
                    sizes="60px"
                    src={getPodcastImage(podcast, ImageSizes.Small)}
                    srcSet={`${getPodcastImage(podcast, ImageSizes.Small)} 240w, ${getPodcastImage(podcast, ImageSizes.Medium)} 480w`}
                    width={60}
                />
            </ArtworkMorph>
            <span className={styles.coverName}>{podcast.name}</span>
        </Link>
    );
};

const Home = observer(() => {
    const { episodeStore, podcastStore, toplistStore } = useStores();

    const loadLatest = useCallback(async () => {
        await episodeStore.loadLatest();
    }, [episodeStore]);

    const loadToplist = useCallback(async () => {
        await toplistStore.loadToplist();
    }, [toplistStore]);

    const loadStats = useCallback(() => {
        if (!podcastStore.stats) {
            podcastStore.loadStats().catch(console.error);
        }
    }, [podcastStore]);

    useEffect(() => {
        loadLatest().catch((e) => console.error(e));
    }, [loadLatest]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    useEffect(() => {
        loadToplist().catch((e) => console.error(e));
    }, [loadToplist]);

    const hasStats = !!podcastStore.stats;
    const defaultNull = <span className={styles.placeholder}></span>;

    const pod_count = hasStats ? podcastStore.stats?.podcasts.toLocaleString("sv-se") : defaultNull;
    const cat_count = hasStats ? podcastStore.stats?.categories.toLocaleString("sv-se") : defaultNull;
    const author_count = hasStats ? podcastStore.stats?.authors.toLocaleString("sv-se") : defaultNull;
    const episode_count = hasStats ? podcastStore.stats?.episodes.toLocaleString("sv-se") : defaultNull;

    const podcasts: Podcast[] = [];
    for (let i = 0; i < toplistStore.entries.length; i++) {
        const entry = toplistStore.entries[i];

        if (entry.podcast) {
            podcasts.push(entry.podcast);
        }
    }

    const twoWeeksAgo = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 14);
        return date.toLocaleDateString("sv-se");
    }, []);

    const today = useMemo(() => {
        return new Date().toLocaleDateString("sv-se");
    }, []);

    return (
        <div className={styles.wrapper}>
            {useSEO({
                title: "Hitta Podd - Populära Podcasts - HittaPodd.se",
                description: "Hitta podcasts att lyssna på. För oss som älskar poddar. HittaPodd.se",
                canonicalUrl: canonicalUrl(Urls.index()),
            })}
            <section className={styles.hero}>
                <div className={styles.heroCopy}>
                    <Anchor className={styles.heroEyebrow} component={Link} to="/topplista" underline="never">
                        Mer från topplistan
                    </Anchor>
                    <h1>Hitta din nästa podcast</h1>
                    <p>Upptäck svenska poddar, utforska topplistan och hitta nya avsnitt att lyssna på.</p>
                    <div className={styles.heroActions}>
                        <Button color="teal" component={Link} to="/poddar" radius="md" size="md">
                            Senast tillagda poddar
                        </Button>
                        <Button color="gray" component={Link} to="/topplista" radius="md" size="md" variant="default">
                            Topplistan
                        </Button>
                    </div>
                </div>
                <div className={styles.coverGallery} aria-label="Mer från topplistan">
                    {podcasts.slice(0, 4).map((podcast, index) => (
                        <GalleryCover index={index} key={podcast.id ?? podcast.slug} podcast={podcast} />
                    ))}
                </div>
            </section>

            <SignInPrompt />
            <ContinueListening />

            <Grid>
                <Grid.Col span={{ base: 12, md: 8 }}>
                    <div className={styles.latestSection}>
                        <div className={styles.sectionHeading}>
                            <div>
                                <Text className={styles.eyebrow}>HITTA NÅGOT NYTT</Text>
                                <Title order={2}>Nya avsnitt</Title>
                            </div>
                            <Anchor component={Link} to="/poddar" underline="hover">
                                Alla poddar
                            </Anchor>
                        </div>
                        <ul className={rowStyles.list}>
                            {episodeStore.latest.map((episode: Episode, index: number) =>
                                episode.podcast ? (
                                    <EpisodeRow
                                        episode={episode}
                                        key={`home-latest-${episode.id}-${index}`}
                                        podcast={episode.podcast}
                                        showPodcast
                                    />
                                ) : null
                            )}
                        </ul>

                        <Button
                            className={styles.more}
                            color="teal"
                            disabled={episodeStore.loading}
                            fullWidth
                            loading={episodeStore.getting}
                            onClick={(event) => {
                                event.preventDefault();
                                episodeStore.loadMore().catch(console.error);
                            }}
                            radius="md"
                            variant="light">
                            Hämta fler avsnitt
                        </Button>
                    </div>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 4 }}>
                    <Box mb="md">
                        <FeaturedCollections />
                    </Box>
                    <div className={styles.stats}>
                        <Text className={styles.statsTitle}>Om HittaPodd.se</Text>
                        <strong>HittaPodd.se</strong> hjälper dig att hitta <strong>podcasts</strong> att lyssna på. För
                        oss som älskar <strong>poddar</strong> och gärna hittar nya intressanta att lyssna på. Hitta och
                        lyssna på totalt <strong>{pod_count}</strong> st poddar på svenska inom{" "}
                        <strong>{cat_count}</strong> st kategorier från <strong>{author_count}</strong> st utgivare -
                        Sammanlagt <strong>{episode_count}</strong> st episoder. Kör så det ryker. HittaPodd.se är ett
                        hobbyprojekt från{" "}
                        <a href={"https://yetric.se"} target={"out"}>
                            Yetric AB
                        </a>
                        .
                    </div>

                    <div className={styles.quickLinksWrap}>
                        <MantinePaper className={styles.quickLinksCard} p="md" radius="md" withBorder>
                            <Text fw={700} mb="sm">
                                Länkar
                            </Text>
                            <Stack gap={0}>
                                <Anchor className={styles.quickLink} component={Link} to={"/poddar"} underline="never">
                                    Senast tillagda poddar
                                </Anchor>
                                <Divider />
                                <Anchor className={styles.quickLink} component={Link} to={"/samling"} underline="never">
                                    Poddsamlingar
                                </Anchor>
                                <Divider />
                                <Anchor
                                    className={styles.quickLink}
                                    component={Link}
                                    to={"/topplista"}
                                    underline="never">
                                    Topplistan
                                </Anchor>
                            </Stack>
                        </MantinePaper>
                    </div>
                </Grid.Col>
            </Grid>

            {hasStats && (
                <div className={styles.popularSection}>
                    <Title order={2}>Populärt på HittaPodd.se</Title>
                    <Text c="dimmed" size="sm">
                        Baserat på data från {twoWeeksAgo} till {today}
                    </Text>
                    <Grid>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <div className={styles.feedSection}>
                                <Title order={4}>Populära poddar</Title>
                                <PopularPodcasts podcasts={podcastStore.popularPodcasts} />
                            </div>
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                            <div className={styles.feedSection}>
                                <Title order={4}>Populära avsnitt</Title>
                                <PopularEpisodes episodes={podcastStore.popularEpisodes} />
                            </div>
                        </Grid.Col>
                    </Grid>
                </div>
            )}
        </div>
    );
});
export default Home;
